import { useState, useEffect } from 'react'
import {
  X, Package, User, Calendar, MapPin, Euro, Truck, Image,
  Clock, CheckCircle, XCircle, FileText, Download, ExternalLink,
  MessageSquare, Send, Eye, EyeOff, Trash2, ChevronDown, ChevronUp,
  Copy, Check, ChevronLeft, ChevronRight, ArrowLeftRight, RefreshCw
} from 'lucide-react'
import api from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'

interface Props {
  isOpen: boolean
  onClose: () => void
  returnId: number
  onStatusChanged?: () => void
  onOpenAction?: (action: string) => void
}

interface ReturnDetail {
  id: number
  code: string
  order_id: number
  order_reference: string
  order_date: string
  customer_id: number
  customer_email: string
  customer_name: string
  customer_phone: string | null
  customer_type: string
  customer_nif: string | null
  pickup_address: any
  status: string
  reason: string
  reason_details: string | null
  product_amount: number
  shipping_cost: number
  refund_amount: number
  carrier_code: string | null
  pickup_tracking: string | null
  test_result: string
  test_notes: string | null
  pickup_notes: string | null
  shipping_option: string | null
  dropoff_point: any
  refund_method: string | null
  refund_reference: string | null
  refund_iban: string | null
  refunded_at: string | null
  rectificative_invoice: string | null
  replacement_carrier: string | null
  replacement_tracking: string | null
  replacement_notes: string | null
  replaced_at: string | null
  rejection_reason: string | null
  payment_method: string | null
  created_at: string
  fees?: Array<{ id: number; kind: string; amount: number; status: string; paid_at: string | null; paid_via: string | null; refunded_at: string | null; refund_reference: string | null; notes: string | null; payment_url: string | null }>
  items: any[]
  photos: any[]
  history: any[]
  notifications: any[]
  comments: any[]
  has_label: boolean
  has_replacement_label: boolean
  has_rectificative_pdf: boolean
  case_type: string
  incident_carrier: string | null
  incident_subtype: string | null
  incident_subtype_other: string | null
  incident_tracking: string | null
  is_canje: boolean
}

interface Comment {
  id: number
  return_id: number
  user_id: number | null
  user_name: string
  comment: string
  is_public: boolean
  created_at: string
}

const STATUS_LABELS: Record<string, string> = {
  requested: 'Solicitud recibida',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  pickup_scheduled: 'Recogida programada',
  in_transit: 'En transito',
  received: 'Recibida en almacen',
  tested: 'Testeada',
  refunded: 'Reembolsada',
  replacement: 'Cambio por fallo',
  refund_rejected: 'Reembolso rechazado',
  cancelled: 'Cancelada'
}

const STATUS_ORDER = [
  'requested', 'approved', 'pickup_scheduled', 'in_transit',
  'received', 'tested', 'refunded'
]

const STATUS_COLORS: Record<string, string> = {
  requested: 'bg-yellow-500',
  approved: 'bg-blue-500',
  rejected: 'bg-red-500',
  pickup_scheduled: 'bg-cyan-500',
  in_transit: 'bg-purple-500',
  received: 'bg-indigo-500',
  tested: 'bg-teal-500',
  refunded: 'bg-green-500',
  replacement: 'bg-orange-500',
  refund_rejected: 'bg-rose-600',
  cancelled: 'bg-slate-500'
}

const TEST_RESULT_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'text-slate-500' },
  ok: { label: 'OK', color: 'text-green-600' },
  defective: { label: 'Defectuoso', color: 'text-orange-600' },
  damaged: { label: 'Dañado', color: 'text-red-600' },
  fake: { label: 'No corresponde', color: 'text-red-700' },
  warranty_confirmed: { label: 'Defecto confirmado', color: 'text-green-600' },
  warranty_wear: { label: 'Desgaste / mal uso', color: 'text-orange-600' },
  warranty_not_reproducible: { label: 'Fallo no reproducible', color: 'text-amber-600' },
  warranty_no_defect: { label: 'Sin defecto', color: 'text-red-600' },
}

const PAYMENT_LABELS: Record<string, string> = {
  redsys: 'Tarjeta (Redsys)',
  bankwire: 'Transferencia bancaria',
  cashondelivery: 'Contrareembolso',
  paypal: 'PayPal',
  stripe: 'Stripe',
  bizum: 'Bizum'
}

const STATUS_BANNER_CONFIG: Record<string, { bg: string; border: string; text: string; icon: React.ElementType }> = {
  requested: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-800 dark:text-amber-300', icon: Clock },
  approved: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-300', icon: CheckCircle },
  rejected: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-800 dark:text-red-300', icon: XCircle },
  pickup_scheduled: { bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-200 dark:border-cyan-800', text: 'text-cyan-800 dark:text-cyan-300', icon: Truck },
  in_transit: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-800 dark:text-purple-300', icon: Truck },
  received: { bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-800', text: 'text-indigo-800 dark:text-indigo-300', icon: Package },
  tested: { bg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-800', text: 'text-teal-800 dark:text-teal-300', icon: CheckCircle },
  refunded: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-800 dark:text-green-300', icon: Euro },
  replacement: { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-800 dark:text-orange-300', icon: Package },
  cancelled: { bg: 'bg-slate-50 dark:bg-slate-900/20', border: 'border-slate-200 dark:border-slate-800', text: 'text-slate-800 dark:text-slate-300', icon: XCircle }
}

const getQuickActions = (status: string, caseType?: string): { label: string; action: string; bgColor: string }[] => {
  switch (status) {
    case 'requested':
      return [
        { label: 'Aprobar', action: 'approve', bgColor: 'bg-green-500 hover:bg-green-600 text-white' },
        { label: 'Rechazar', action: 'reject', bgColor: 'bg-red-500 hover:bg-red-600 text-white' }
      ]
    case 'approved':
    case 'pickup_scheduled':
    case 'in_transit':
      return [{ label: 'Marcar recibida', action: 'receive', bgColor: 'bg-indigo-500 hover:bg-indigo-600 text-white' }]
    case 'received':
      return [{ label: 'Registrar testeo', action: 'test', bgColor: 'bg-violet-500 hover:bg-violet-600 text-white' }]
    case 'tested':
      if (caseType === 'warranty') {
        return [
          { label: 'Garantía procedente', action: 'warranty_approve', bgColor: 'bg-green-500 hover:bg-green-600 text-white' },
          { label: 'Garantía no procedente', action: 'warranty_reject', bgColor: 'bg-red-500 hover:bg-red-600 text-white' }
        ]
      }
      if (caseType === 'doa' || caseType === 'transport') {
        return [
          { label: 'Cambio por fallo', action: 'replacement', bgColor: 'bg-orange-500 hover:bg-orange-600 text-white' }
        ]
      }
      return [
        { label: 'Procesar reembolso', action: 'refund', bgColor: 'bg-green-500 hover:bg-green-600 text-white' },
        { label: 'Cambio por fallo', action: 'replacement', bgColor: 'bg-orange-500 hover:bg-orange-600 text-white' },
        { label: 'Rechazar reembolso', action: 'reject_refund', bgColor: 'bg-red-500 hover:bg-red-600 text-white' }
      ]
    default:
      return []
  }
}

function formatIban(iban: string): string {
  const clean = iban.replace(/\s/g, '')
  return clean.replace(/(.{4})/g, '$1 ').trim()
}

function IbanDisplay({ iban }: { iban: string }) {
  const [copied, setCopied] = useState(false)
  const copyIban = () => {
    navigator.clipboard.writeText(iban.replace(/\s/g, ''))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
      <span className="text-slate-500 text-sm">IBAN cliente:</span>
      <div className="flex items-center gap-2 mt-1">
        <code className="bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded font-mono text-sm tracking-wide">
          {formatIban(iban)}
        </code>
        <button
          onClick={copyIban}
          className="p-1.5 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          title="Copiar IBAN"
        >
          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
        </button>
      </div>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-4" />
            <div className="space-y-2.5">
              <div className="h-3.5 bg-slate-100 dark:bg-slate-700/60 rounded w-full" />
              <div className="h-3.5 bg-slate-100 dark:bg-slate-700/60 rounded w-3/4" />
              <div className="h-3.5 bg-slate-100 dark:bg-slate-700/60 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16 mb-3" />
        <div className="h-3.5 bg-slate-100 dark:bg-slate-700/60 rounded w-full" />
      </div>
    </div>
  )
}

// Unified card component for consistent styling
function InfoCard({ icon: Icon, title, iconColor, children }: {
  icon: React.ElementType
  title: string
  iconColor: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
      <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2 text-sm">
        <div className={`p-1.5 rounded-lg ${iconColor}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        {title}
      </h3>
      <div className="space-y-2 text-sm">
        {children}
      </div>
    </div>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-slate-500 dark:text-slate-400 shrink-0 text-xs">{label}</span>
      <span className="text-slate-900 dark:text-white">{children}</span>
    </div>
  )
}

export default function ReturnDetailModal({ isOpen, onClose, returnId, onStatusChanged, onOpenAction }: Props) {
  const [detail, setDetail] = useState<ReturnDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'info' | 'timeline' | 'comments'>('info')
  const [activePhotoTab, setActivePhotoTab] = useState<'product' | 'packaging' | 'all'>('all')
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null)

  // Comments
  const [newComment, setNewComment] = useState('')
  const [commentIsPublic, setCommentIsPublic] = useState(false)
  const [submittingComment, setSubmittingComment] = useState(false)

  // Manual status change
  const [showStatusChange, setShowStatusChange] = useState(false)
  const [newStatus, setNewStatus] = useState('')
  const [statusNote, setStatusNote] = useState('')
  const [changingStatus, setChangingStatus] = useState(false)

  const { user } = useAuth()
  const authToken = localStorage.getItem('jvs_auth_token') || ''

  const photoUrl = (photoId: number) =>
    `/api/returns/admin/${returnId}/photos/${photoId}?token=${encodeURIComponent(authToken)}`

  useEffect(() => {
    if (isOpen && returnId) {
      loadDetail()
      setActiveTab('info')
    }
  }, [isOpen, returnId])

  const loadDetail = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/returns/admin/${returnId}`)
      setDetail(response.data)
    } catch (error) {
      console.error('Error loading return detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const downloadLabel = async () => {
    try {
      const response = await fetch(`/api/returns/admin/${returnId}/label`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jvs_auth_token')}` }
      })
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `etiqueta_${detail?.code}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading label:', error)
    }
  }

  const downloadRectificativePdf = async () => {
    try {
      const response = await fetch(`/api/returns/admin/${returnId}/rectificative-pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jvs_auth_token')}` }
      })
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Rectificativa_2-${detail?.rectificative_invoice}_${detail?.code}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading rectificative PDF:', error)
    }
  }

  const [regeneratingRect, setRegeneratingRect] = useState(false)
  const [feeBusy, setFeeBusy] = useState(false)

  // ---- Cobros y garantía no procedente (política 7-sep-2026) ----
  const feeAction = async (label: string, url: string, body: any = {}) => {
    if (!confirm(label + ' ¿Continuar?')) return
    setFeeBusy(true)
    try {
      await api.post(url, body)
      await loadDetail()
      onStatusChanged?.()
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error')
    } finally {
      setFeeBusy(false)
    }
  }
  const FEE_KIND: Record<string, string> = { pickup: 'Recogida (garantía)', reshipment: 'Reenvío (garantía no procedente)' }
  const FEE_STATUS: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Pendiente de pago', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
    paid: { label: 'Pagado', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
    refunded: { label: 'Devuelto al cliente', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
    waived: { label: 'Perdonado', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
    cancelled: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300' },
  }

  const regenerateRectificative = async () => {
    if (!confirm('¿Generar factura rectificativa para esta devolución?')) return
    setRegeneratingRect(true)
    try {
      await api.post(`/returns/admin/${returnId}/regenerate-rectificative`)
      await loadDetail()
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error generando rectificativa')
    } finally {
      setRegeneratingRect(false)
    }
  }

  const downloadReplacementLabel = async () => {
    try {
      const response = await fetch(`/api/returns/admin/${returnId}/replacement-label`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jvs_auth_token')}` }
      })
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `etiqueta_sustitucion_${detail?.code}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading replacement label:', error)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return
    setSubmittingComment(true)
    try {
      await api.post(`/returns/admin/${returnId}/comments`, {
        comment: newComment.trim(),
        is_public: commentIsPublic
      })
      setNewComment('')
      setCommentIsPublic(false)
      await loadDetail()
    } catch (error) {
      console.error('Error adding comment:', error)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleDeleteComment = async (commentId: number) => {
    try {
      await api.delete(`/returns/admin/${returnId}/comments/${commentId}`)
      await loadDetail()
    } catch (error) {
      console.error('Error deleting comment:', error)
    }
  }

  const handleChangeStatus = async () => {
    if (!newStatus) return
    setChangingStatus(true)
    try {
      await api.post(`/returns/admin/${returnId}/status`, {
        new_status: newStatus,
        note: statusNote || undefined
      })
      setShowStatusChange(false)
      setNewStatus('')
      setStatusNote('')
      await loadDetail()
      onStatusChanged?.()
    } catch (error: any) {
      console.error('Error changing status:', error)
    } finally {
      setChangingStatus(false)
    }
  }

  const filteredPhotos = detail?.photos.filter(p => {
    if (activePhotoTab === 'all') return true
    return p.photo_type === activePhotoTab
  }) || []

  // Photo navigation in lightbox
  const navigatePhoto = (direction: 'prev' | 'next') => {
    if (selectedPhotoIndex === null) return
    if (direction === 'prev' && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1)
    } else if (direction === 'next' && selectedPhotoIndex < filteredPhotos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1)
    }
  }

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (selectedPhotoIndex === null) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigatePhoto('prev')
      else if (e.key === 'ArrowRight') navigatePhoto('next')
      else if (e.key === 'Escape') setSelectedPhotoIndex(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedPhotoIndex, filteredPhotos.length])

  const currentStatusIndex = detail ? STATUS_ORDER.indexOf(detail.status) : -1

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {detail?.code || '...'}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Pedido #{detail?.order_reference}
                {detail?.payment_method && (
                  <span className="ml-2 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs">
                    {PAYMENT_LABELS[detail.payment_method] || detail.payment_method}
                  </span>
                )}
              </p>
            </div>
            {detail && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${STATUS_COLORS[detail.status] || 'bg-slate-500'}`}>
                {STATUS_LABELS[detail.status] || detail.status}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
          {([
            { id: 'info', label: 'Detalle', icon: Package },
            { id: 'timeline', label: 'Estados', icon: Clock },
            { id: 'comments', label: `Mensajes${detail?.comments?.length ? ` (${detail.comments.length})` : ''}`, icon: MessageSquare }
          ] as const).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status Banner */}
        {detail && (() => {
          const bannerCfg = STATUS_BANNER_CONFIG[detail.status] || STATUS_BANNER_CONFIG.requested
          const BannerIcon = bannerCfg.icon
          const quickActions = getQuickActions(detail.status, detail.case_type)
          return (
            <div className={`mx-6 mt-4 rounded-xl border p-4 ${bannerCfg.bg} ${bannerCfg.border}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${bannerCfg.bg}`}>
                    <BannerIcon className={`w-5 h-5 ${bannerCfg.text}`} />
                  </div>
                  <div>
                    <p className={`font-bold ${bannerCfg.text}`}>
                      {STATUS_LABELS[detail.status] || detail.status}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDate(detail.created_at)}
                    </p>
                  </div>
                </div>
                {quickActions.length > 0 && onOpenAction && (
                  <div className="flex gap-2">
                    {quickActions.map((qa, idx) => (
                      <button
                        key={idx}
                        onClick={() => { onClose(); onOpenAction(qa.action) }}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${qa.bgColor}`}
                      >
                        {qa.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })()}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
          {loading ? (
            <DetailSkeleton />
          ) : detail ? (
            <>
              {/* ============ TAB: INFO ============ */}
              {activeTab === 'info' && (
                <div className="space-y-4">
                  {/* Info cards grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Cliente */}
                    <InfoCard icon={User} title="Cliente" iconColor="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400">
                      <InfoRow label="Nombre:">{detail.customer_name}</InfoRow>
                      <InfoRow label="Email:">{detail.customer_email}</InfoRow>
                      <InfoRow label="Telefono:">{detail.customer_phone || 'No disponible'}</InfoRow>
                      {detail.customer_nif && (
                        <InfoRow label="NIF/CIF:">{detail.customer_nif}</InfoRow>
                      )}
                      <InfoRow label="Tipo:">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          detail.customer_type === 'VIP' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                          detail.customer_type === 'PVD' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                          'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {detail.customer_type}
                        </span>
                      </InfoRow>
                    </InfoCard>

                    {/* Importes */}
                    <InfoCard icon={Euro} title="Importes" iconColor="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400">
                      <InfoRow label="Productos:">{formatMoney(detail.product_amount)}</InfoRow>
                      {detail.case_type === 'return' && (
                        <>
                          <InfoRow label="Envio:">{formatMoney(detail.shipping_cost)}</InfoRow>
                          <div className="pt-2 mt-2 border-t border-slate-100 dark:border-slate-700">
                            <div className="flex items-baseline gap-2">
                              <span className="text-slate-500 dark:text-slate-400 shrink-0 text-xs">Reembolso:</span>
                              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatMoney(detail.refund_amount)}</span>
                            </div>
                          </div>
                          {detail.payment_method && (
                            <InfoRow label="Pago original:">{PAYMENT_LABELS[detail.payment_method] || detail.payment_method}</InfoRow>
                          )}
                          {detail.refund_iban && (
                            <IbanDisplay iban={detail.refund_iban} />
                          )}
                        </>
                      )}
                    </InfoCard>

                    {/* Direccion */}
                    <InfoCard icon={MapPin} title="Direccion" iconColor="bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                      {detail.pickup_address ? (
                        <div className="space-y-1">
                          <p className="text-slate-900 dark:text-white">{detail.pickup_address.street}</p>
                          <p className="text-slate-900 dark:text-white">{detail.pickup_address.postal_code} {detail.pickup_address.city}</p>
                          <p className="text-slate-500 dark:text-slate-400">{detail.pickup_address.country_code}</p>
                          {detail.pickup_address.phone && (
                            <p className="text-slate-500 dark:text-slate-400">Tel: {detail.pickup_address.phone}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-slate-500 dark:text-slate-400">No disponible</p>
                      )}
                      {detail.dropoff_point && (
                        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                          <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">Punto de entrega CTT</p>
                          <p className="text-slate-900 dark:text-white">{detail.dropoff_point.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{detail.dropoff_point.address}</p>
                        </div>
                      )}
                    </InfoCard>

                    {/* Envio */}
                    <InfoCard icon={Truck} title="Envio" iconColor="bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400">
                      <InfoRow label="Opcion:">{
                        detail.shipping_option === 'dropoff' ? 'Punto de entrega' :
                        detail.shipping_option === 'own' ? 'Envio propio' : 'Recogida a domicilio'
                      }</InfoRow>
                      {detail.carrier_code && (
                        <InfoRow label="Transportista:">{detail.carrier_code.toUpperCase()}</InfoRow>
                      )}
                      {detail.pickup_tracking && (
                        <InfoRow label="Tracking:">
                          <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">{detail.pickup_tracking}</code>
                        </InfoRow>
                      )}
                      {detail.has_label && (
                        <button onClick={downloadLabel}
                          className="mt-2 inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors">
                          <Download className="w-4 h-4" />
                          Descargar etiqueta
                        </button>
                      )}
                    </InfoCard>
                  </div>

                  {/* Cobros (recogida en garantía / reenvío) */}
                  {detail.fees && detail.fees.length > 0 && (
                    <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 p-4">
                      <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-3 flex items-center gap-2 text-sm">
                        <Euro className="w-4 h-4" /> Cobros al cliente
                      </h3>
                      <div className="space-y-2">
                        {detail.fees.map(f => (
                          <div key={f.id} className="flex flex-wrap items-center justify-between gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border border-amber-100 dark:border-slate-700">
                            <div className="text-sm">
                              <span className="font-medium text-slate-900 dark:text-white">{FEE_KIND[f.kind] || f.kind}</span>
                              <span className="ml-2 font-mono">{formatMoney(f.amount)}</span>
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs ${FEE_STATUS[f.status]?.cls || ''}`}>{FEE_STATUS[f.status]?.label || f.status}</span>
                              {f.paid_via && <span className="ml-2 text-xs text-slate-500">vía {f.paid_via}</span>}
                              {f.refund_reference && <span className="ml-2 text-xs text-slate-500">ref {f.refund_reference}</span>}
                              {f.notes && <p className="text-xs text-slate-500 mt-0.5">{f.notes}</p>}
                            </div>
                            <div className="flex gap-2">
                              {f.status === 'pending' && f.payment_url && (
                                <button onClick={() => { navigator.clipboard.writeText(f.payment_url!); alert('Enlace de pago copiado') }}
                                  className="px-2.5 py-1 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200">Copiar enlace</button>
                              )}
                              {f.status === 'pending' && (
                                <>
                                  <button disabled={feeBusy} onClick={() => feeAction('Marcar como pagado (transferencia / en tienda).', `/returns/admin/fees/${f.id}/confirm`, { paid_via: 'admin' })}
                                    className="px-2.5 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">Pagado a mano</button>
                                  <button disabled={feeBusy} onClick={() => { const r = prompt('Motivo para perdonar el cobro:') ?? ''; feeAction('Perdonar el cobro.', `/returns/admin/fees/${f.id}/waive`, { reason: r }) }}
                                    className="px-2.5 py-1 text-xs rounded bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-50">Perdonar</button>
                                </>
                              )}
                              {f.status === 'paid' && (
                                <button disabled={feeBusy} onClick={() => { const r = prompt('Referencia de la devolución (opcional):') ?? ''; feeAction('Marcar el cobro como devuelto a mano al cliente.', `/returns/admin/fees/${f.id}/refund`, { reference: r || null }) }}
                                  className="px-2.5 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">Devuelto a mano</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Garantía no procedente: cierre del caso */}
                  {detail.case_type === 'warranty' && detail.status === 'warranty_rejected' && (
                    <div className="rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-slate-800/50 p-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-1 text-sm">Garantía no procedente: ¿qué hacemos con el producto?</h3>
                      <p className="text-xs text-slate-500 mb-3">
                        {detail.customer_type === 'VIP'
                          ? 'Cliente VIP: reenvío sin cargo.'
                          : 'El reenvío exige que el cobro esté pagado o perdonado. Las otras dos opciones no generan envío.'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button disabled={feeBusy} onClick={() => feeAction('Generar la etiqueta y reenviar el producto al cliente.', `/returns/admin/${returnId}/ship-back`, {})}
                          className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5"><Truck className="w-4 h-4" /> Reenviar al cliente</button>
                        <button disabled={feeBusy} onClick={() => feeAction('Marcar que el cliente ha recogido el producto en tienda.', `/returns/admin/${returnId}/in-store-pickup`, {})}
                          className="px-3 py-1.5 text-sm rounded-lg bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-50">Recogido en tienda</button>
                        <button disabled={feeBusy} onClick={() => { const n = prompt('Nota (opcional):') ?? ''; feeAction('Cerrar el caso: el cliente renuncia al producto.', `/returns/admin/${returnId}/abandon`, { note: n }) }}
                          className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">Renuncia / cerrar</button>
                      </div>
                    </div>
                  )}

                  {/* Observaciones */}
                  {detail.pickup_notes && (
                    <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                      <h3 className="font-semibold text-blue-800 dark:text-blue-400 mb-2 flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4" /> Observaciones
                      </h3>
                      <p className="text-sm text-blue-700 dark:text-blue-300">{detail.pickup_notes}</p>
                    </div>
                  )}

                  {/* Motivo */}
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-2 text-sm">Motivo</h3>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{detail.reason}</p>
                    {detail.reason_details && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{detail.reason_details}</p>
                    )}
                  </div>

                  {/* Rechazo */}
                  {detail.rejection_reason && (
                    <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                      <h3 className="font-semibold text-red-800 dark:text-red-400 mb-2 flex items-center gap-2 text-sm">
                        <XCircle className="w-4 h-4" /> {detail.status === 'refund_rejected' ? 'Motivo del rechazo del reembolso' : 'Motivo del rechazo'}
                      </h3>
                      <p className="text-sm text-red-700 dark:text-red-300">{detail.rejection_reason}</p>
                    </div>
                  )}

                  {/* Testeo */}
                  {detail.test_result !== 'pending' && (
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-2 text-sm">Resultado del Testeo</h3>
                      <p className={`text-sm font-medium ${TEST_RESULT_LABELS[detail.test_result]?.color || ''}`}>
                        {TEST_RESULT_LABELS[detail.test_result]?.label || detail.test_result}
                      </p>
                      {detail.test_notes && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{detail.test_notes}</p>}
                    </div>
                  )}

                  {/* Reembolso (solo devoluciones) */}
                  {detail.case_type === 'return' && detail.refunded_at && (
                    <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
                      <h3 className="font-semibold text-green-800 dark:text-green-400 mb-2 flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4" /> Reembolso Procesado
                      </h3>
                      <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
                        <p>Metodo: {PAYMENT_LABELS[detail.refund_method || ''] || detail.refund_method}</p>
                        <p>Referencia: {detail.refund_reference}</p>
                        <p>Fecha: {formatDate(detail.refunded_at)}</p>
                        {detail.refund_iban && <p>IBAN: {detail.refund_iban}</p>}
                        {detail.rectificative_invoice ? (
                          <div>
                            <p>Factura rectificativa: 2-{detail.rectificative_invoice}</p>
                            {detail.has_rectificative_pdf && (
                              <button onClick={downloadRectificativePdf}
                                className="mt-1 inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors">
                                <Download className="w-4 h-4" />
                                Descargar PDF rectificativa
                              </button>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className="text-amber-600 dark:text-amber-400">Sin factura rectificativa</p>
                            <button onClick={regenerateRectificative} disabled={regeneratingRect}
                              className="mt-1 inline-flex items-center gap-1.5 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 transition-colors disabled:opacity-50">
                              <RefreshCw className={`w-4 h-4 ${regeneratingRect ? 'animate-spin' : ''}`} />
                              {regeneratingRect ? 'Generando...' : 'Generar rectificativa'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Datos incidencia transporte */}
                  {detail.case_type === 'transport' && (detail.incident_carrier || detail.incident_subtype || detail.incident_tracking) && (
                    <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                      <h3 className="font-semibold text-red-800 dark:text-red-400 mb-2 flex items-center gap-2 text-sm">
                        <Truck className="w-4 h-4" /> Envio con incidencia
                      </h3>
                      <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                        {detail.incident_carrier && (
                          <p>Transportista causante: <span className="font-medium">{detail.incident_carrier.toUpperCase()}</span></p>
                        )}
                        {detail.incident_subtype && (
                          <p>Tipo: <span className="font-medium">
                            {detail.incident_subtype === 'loss' ? 'Perdida' : detail.incident_subtype === 'damage' ? 'Rotura' : detail.incident_subtype === 'other' ? (detail.incident_subtype_other || 'Otra') : detail.incident_subtype}
                          </span></p>
                        )}
                        {detail.incident_tracking && (
                          <p>Tracking: <code className="bg-red-100 dark:bg-red-900/40 px-1.5 rounded text-xs">{detail.incident_tracking}</code></p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* MRW Canje (DOA) */}
                  {detail.is_canje && detail.pickup_tracking && (
                    <div className="rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 p-4">
                      <h3 className="font-semibold text-teal-800 dark:text-teal-400 mb-2 flex items-center gap-2 text-sm">
                        <ArrowLeftRight className="w-4 h-4" /> MRW Canje
                      </h3>
                      <div className="text-sm text-teal-700 dark:text-teal-300 space-y-1">
                        <p>Tracking: <code className="bg-teal-100 dark:bg-teal-900/40 px-1.5 rounded text-xs">{detail.pickup_tracking}</code></p>
                        {detail.replaced_at && (
                          <p>Creado: {formatDate(detail.replaced_at)}</p>
                        )}
                        <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                          {detail.status === 'replacement' && 'Pendiente: el mensajero entregara la sustitucion y recogera el defectuoso'}
                          {detail.status === 'in_transit' && 'Canje completado: defectuoso en transito hacia JVS'}
                          {detail.status === 'received' && 'Defectuoso recibido en almacen'}
                          {['tested', 'closed'].includes(detail.status) && 'Proceso completado'}
                        </p>
                        {detail.has_label && (
                          <button onClick={downloadLabel}
                            className="mt-1.5 text-teal-700 dark:text-teal-300 hover:underline flex items-center gap-1 text-xs font-medium">
                            <Download className="w-3.5 h-3.5" />
                            Descargar etiqueta
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cambio por fallo (no canje) */}
                  {!detail.is_canje && detail.replaced_at && (
                    <div className="rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4">
                      <h3 className="font-semibold text-orange-800 dark:text-orange-400 mb-2 flex items-center gap-2 text-sm">
                        <Package className="w-4 h-4" /> Cambio por fallo
                      </h3>
                      <div className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
                        <p>Fecha: {formatDate(detail.replaced_at)}</p>
                        {detail.replacement_carrier && (
                          <p>Transportista: {detail.replacement_carrier.toUpperCase()}</p>
                        )}
                        {detail.replacement_tracking && (
                          <p>Tracking: <code className="bg-orange-100 dark:bg-orange-900/40 px-1.5 rounded text-xs">{detail.replacement_tracking}</code></p>
                        )}
                        {detail.replacement_notes && (
                          <p>Notas: {detail.replacement_notes}</p>
                        )}
                        {detail.has_replacement_label && (
                          <button onClick={downloadReplacementLabel}
                            className="mt-2 inline-flex items-center gap-1.5 text-orange-700 dark:text-orange-300 hover:underline text-xs font-medium">
                            <Download className="w-3.5 h-3.5" />
                            Descargar etiqueta sustitucion
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Productos */}
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2 text-sm">
                      <Package className="w-4 h-4 text-slate-500" /> Productos ({detail.items.length})
                    </h3>
                    <div className="space-y-2">
                      {detail.items.map((item: any) => (
                        <div key={item.id} className="flex items-center gap-4 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
                          {item.product_image && (
                            <img src={item.product_image} alt={item.product_name} className="w-12 h-12 object-cover rounded-lg" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{item.product_name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Ref: {item.product_reference} | Cantidad: {item.quantity}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-sm text-slate-900 dark:text-white">{formatMoney(item.total_price)}</p>
                            {item.test_result !== 'pending' && (
                              <p className={`text-xs ${TEST_RESULT_LABELS[item.test_result]?.color || ''}`}>
                                {TEST_RESULT_LABELS[item.test_result]?.label || item.test_result}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Fotos */}
                  {detail.photos.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2 text-sm">
                        <Image className="w-4 h-4 text-slate-500" /> Fotos ({detail.photos.length})
                      </h3>
                      <div className="flex gap-2 mb-3">
                        {(['all', 'product', 'packaging'] as const).map(tab => (
                          <button key={tab} onClick={() => setActivePhotoTab(tab)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                              activePhotoTab === tab
                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                            }`}>
                            {tab === 'all' ? 'Todas' : tab === 'product' ? 'Producto' : 'Embalaje'}
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                        {filteredPhotos.map((photo: any, idx: number) => (
                          <button key={photo.id}
                            onClick={() => setSelectedPhotoIndex(idx)}
                            className="aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors group">
                            <img src={photoUrl(photo.id)} alt={photo.filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ============ TAB: TIMELINE ============ */}
              {activeTab === 'timeline' && (
                <div className="space-y-6">
                  {/* Timeline visual */}
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-6">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-6 text-sm">Progreso de la devolucion</h3>
                    <div className="relative">
                      {STATUS_ORDER.map((status, idx) => {
                        const isCompleted = currentStatusIndex >= idx
                        const isCurrent = detail.status === status
                        const isRejected = detail.status === 'rejected' || detail.status === 'cancelled'

                        const historyEvent = [...detail.history].reverse().find(h => h.status === status)

                        return (
                          <div key={status} className="flex items-start gap-4 relative">
                            {idx < STATUS_ORDER.length - 1 && (
                              <div className={`absolute left-[17px] top-[36px] w-[3px] h-[calc(100%-8px)] rounded-full ${
                                isCompleted && !isRejected ? 'bg-green-400 dark:bg-green-500' : 'bg-slate-200 dark:bg-slate-700'
                              }`} />
                            )}
                            <div className={`w-[36px] h-[36px] rounded-full flex items-center justify-center shrink-0 z-10 ${
                              isCurrent ? 'bg-indigo-500 ring-4 ring-indigo-100 dark:ring-indigo-900' :
                              isCompleted && !isRejected ? 'bg-green-500' :
                              'bg-slate-200 dark:bg-slate-700'
                            }`}>
                              {isCompleted && !isCurrent && !isRejected ? (
                                <CheckCircle className="w-5 h-5 text-white" />
                              ) : (
                                <div className={`w-2.5 h-2.5 rounded-full ${isCurrent ? 'bg-white' : 'bg-slate-400'}`} />
                              )}
                            </div>
                            <div className={`pb-8 flex-1 ${!isCompleted && !isCurrent ? 'opacity-40' : ''}`}>
                              <p className={`font-medium text-sm ${isCurrent ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-900 dark:text-white'}`}>
                                {STATUS_LABELS[status]}
                              </p>
                              {historyEvent && (
                                <div className="mt-1">
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{formatDate(historyEvent.created_at)}</p>
                                  {historyEvent.created_by && (
                                    <p className="text-xs text-slate-400 dark:text-slate-500">por {historyEvent.created_by}</p>
                                  )}
                                  {historyEvent.description && historyEvent.description !== STATUS_LABELS[status] && (
                                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{historyEvent.description}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}

                      {(detail.status === 'rejected' || detail.status === 'cancelled') && (
                        <div className="flex items-start gap-4 relative">
                          <div className="w-[36px] h-[36px] rounded-full flex items-center justify-center shrink-0 z-10 bg-red-500 ring-4 ring-red-100 dark:ring-red-900">
                            <XCircle className="w-5 h-5 text-white" />
                          </div>
                          <div className="pb-4 flex-1">
                            <p className="font-medium text-sm text-red-600 dark:text-red-400">{STATUS_LABELS[detail.status]}</p>
                            {detail.rejection_reason && (
                              <p className="text-xs text-red-500 dark:text-red-400 mt-1">{detail.rejection_reason}</p>
                            )}
                            {detail.history.find(h => h.status === detail.status) && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                {formatDate(detail.history.find(h => h.status === detail.status)!.created_at)}
                                {detail.history.find(h => h.status === detail.status)!.created_by &&
                                  ` - por ${detail.history.find(h => h.status === detail.status)!.created_by}`}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manual status change */}
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4">
                    <button
                      onClick={() => setShowStatusChange(!showStatusChange)}
                      className="flex items-center justify-between w-full text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                      <span>Cambiar estado manualmente</span>
                      {showStatusChange ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {showStatusChange && (
                      <div className="mt-4 space-y-3">
                        <select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                        >
                          <option value="">Seleccionar nuevo estado...</option>
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value} disabled={value === detail.status}>
                              {label} {value === detail.status ? '(actual)' : ''}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={statusNote}
                          onChange={(e) => setStatusNote(e.target.value)}
                          placeholder="Nota (opcional)..."
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                        />
                        <button
                          onClick={handleChangeStatus}
                          disabled={!newStatus || changingStatus}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                        >
                          {changingStatus ? 'Cambiando...' : 'Aplicar cambio'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Full history */}
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-3 text-sm">Historial completo</h3>
                    <div className="space-y-2">
                      {detail.history.map((event: any, idx: number) => (
                        <div key={idx} className="flex items-start gap-3 text-sm p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
                          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${STATUS_COLORS[event.status] || 'bg-slate-400'}`} />
                          <div className="flex-1">
                            <p className="text-slate-900 dark:text-white">{event.description}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              {formatDate(event.created_at)}
                              {event.created_by && (
                                <span className="ml-2 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-400">
                                  {event.created_by}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ============ TAB: COMMENTS ============ */}
              {activeTab === 'comments' && (
                <div className="space-y-4">
                  <div className="space-y-3">
                    {(!detail.comments || detail.comments.length === 0) && (
                      <div className="text-center py-12">
                        <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                        <p className="text-sm text-slate-400 dark:text-slate-500">No hay mensajes</p>
                      </div>
                    )}
                    {detail.comments?.map((c: Comment) => (
                      <div key={c.id} className={`p-4 rounded-xl border ${
                        c.is_public
                          ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                          : 'bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-medium text-slate-700 dark:text-slate-300">{c.user_name}</span>
                            <span className="text-slate-400 dark:text-slate-500">{formatDate(c.created_at)}</span>
                            {c.is_public ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded text-xs">
                                <Eye className="w-3 h-3" /> Publico
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded text-xs">
                                <EyeOff className="w-3 h-3" /> Interno
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteComment(c.id)}
                            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 mt-2 whitespace-pre-wrap">{c.comment}</p>
                      </div>
                    ))}
                  </div>

                  {/* New comment */}
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                               bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm resize-none
                               focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={commentIsPublic}
                          onChange={(e) => setCommentIsPublic(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <Eye className="w-4 h-4" />
                        Visible para el cliente
                      </label>
                      <button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || submittingComment}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm
                                 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        <Send className="w-4 h-4" />
                        {submittingComment ? 'Enviando...' : 'Enviar'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-slate-500 dark:text-slate-400 py-12">No se encontro la devolucion</p>
          )}
        </div>
      </div>

      {/* Photo Lightbox with navigation */}
      {selectedPhotoIndex !== null && filteredPhotos[selectedPhotoIndex] && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
          onClick={() => setSelectedPhotoIndex(null)}
        >
          {/* Previous */}
          {selectedPhotoIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigatePhoto('prev') }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-10"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Image */}
          <img
            src={photoUrl(filteredPhotos[selectedPhotoIndex].id)}
            alt="Foto ampliada"
            className="max-w-[85vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          {selectedPhotoIndex < filteredPhotos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigatePhoto('next') }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors z-10"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Close + counter */}
          <div className="absolute top-4 right-4 flex items-center gap-3">
            <span className="text-white/70 text-sm tabular-nums">
              {selectedPhotoIndex + 1} / {filteredPhotos.length}
            </span>
            <button onClick={() => setSelectedPhotoIndex(null)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
